import { RouteProp, useRoute } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormProvider } from 'react-hook-form';
import {
  Platform,
  Pressable,
  RefreshControl,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Scroll, Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { auth } from '@/features/auth/types';
import {
  useAddFavoriteCoachesMutation,
  useCreateGuardianPlayerMutation,
  useGuardianProfileQuery,
  useUpdateGuardianProfileMutation,
  useUpdateNotificationPreferencesMutation,
  useUpdateOnboardingProgressMutation,
} from '@/features/guardian/queries';
import { guardianKeys } from '@/features/guardian/queries/query-keys';
import {
  OnboardingFooter,
  OnboardingStepFindAcademy,
  OnboardingStepMatchPlayers,
  OnboardingStepProfile,
  OnboardingStepStayInLoop,
  OnboardingStepper,
} from '@/features/onboarding/components';
import { useOnboarding } from '@/features/onboarding/hooks/use-onboarding';
import { useOnboardingProfileForm } from '@/features/onboarding/hooks/use-onboarding-profile-form';
import { useOnboardingPlayersStore } from '@/features/onboarding/stores/onboarding-players-store';
import { mapOnboardingProfileToGuardianRequest } from '@/features/onboarding/utils/map-onboarding-profile';
import { createStyles, useGuardianStackNavigation, useIsWindowed, useScaling } from '@/hooks';
import { uploadImageToStorage } from '@/libs/firebase/storage/storage-helpers';
import { captureException } from '@/libs/logger';
import type { GuardianStackParamList } from '@/navigations/types';
import { useUiStore } from '@/stores';
import { useSessionStore } from '@/stores/session-store';
import { colors } from '@/theme/colors';
import { shouldUploadAvatarUri } from '@/utils/helper';
import { useShallow } from 'zustand/shallow';

/** Light diagonal gradient shared with the auth flow (matches Figma background). */
const BACKGROUND_GRADIENT = ['#EEF3FF', '#F6F8FC', '#EEF3FF'];
const BACKGROUND_LOCATIONS = [0.08, 0.47, 0.91];

/**
 * Guardian onboarding wizard. Fixed stepper at the top and action bar at the bottom
 * frame a scrollable body that swaps between the four step components: profile,
 * manage players, find a coach, and notification/Face ID preferences.
 *
 * The entry screen is gated upstream (splash / role switch) on the guardian's saved
 * progress, which resumes the wizard via the `initialStep` route param.
 */
export function OnboardingScreen() {
  const { scale } = useScaling();
  const insets = useSafeAreaInsets();
  const isWindowed = useIsWindowed();
  const styles = useStyles();
  const navigation = useGuardianStackNavigation();
  const { params } = useRoute<RouteProp<GuardianStackParamList, 'Onboarding'>>();

  // Finishing (or skipping through) the last step lands on the "You're all set!"
  // confirmation screen; its "Take me home" button moves on to the dashboard.
  const goToFinish = React.useCallback(() => {
    navigation.replace('OnboardingFinish');
  }, [navigation]);

  const { currentStep, totalSteps, isFirstStep, isLastStep, goNext, goBack } = useOnboarding({
    onComplete: goToFinish,
    initialStep: params?.initialStep ?? 0,
  });

  const profileForm = useOnboardingProfileForm();

  const uid = useSessionStore(s => s.user?.uid);
  const queryClient = useQueryClient();
  const { data: guardianProfile } = useGuardianProfileQuery(uid);

  // Step 2 pull-to-refresh (native only — RN's RefreshControl has no web pull gesture).
  // Refetches the linked-players list and bumps a cache-bust nonce so each player's avatar
  // re-fetches even when its URL is unchanged (RN Image caches by URL, so a data refetch
  // alone leaves stale avatars on screen).
  const [refreshingPlayers, setRefreshingPlayers] = React.useState(false);
  const [avatarCacheBust, setAvatarCacheBust] = React.useState(0);
  const handleRefreshPlayers = React.useCallback(async () => {
    setRefreshingPlayers(true);
    try {
      await queryClient.refetchQueries({ queryKey: guardianKeys.players(uid), exact: true });
      setAvatarCacheBust(nonce => nonce + 1);
    } finally {
      setRefreshingPlayers(false);
    }
  }, [queryClient, uid]);
  const { mutateAsync: updateGuardian } = useUpdateGuardianProfileMutation(uid);
  const { mutateAsync: updateOnboarding } = useUpdateOnboardingProgressMutation(uid);
  const { mutateAsync: createPlayer } = useCreateGuardianPlayerMutation(uid);
  const { mutateAsync: addFavoriteCoaches } = useAddFavoriteCoachesMutation(uid);
  const { mutateAsync: updateNotificationPreferences } =
    useUpdateNotificationPreferencesMutation(uid);

  // Step 3 coach selection. Owned here (not in the step component) so it survives the
  // step's unmount/remount when the guardian moves between steps and comes back — the
  // step is rendered controlled off this value. Starts empty: nothing is pre-selected
  // until the guardian either has a saved favorite or taps a coach.
  const initialFavoriteCoachId = guardianProfile?.favoriteCoaches?.[0] ?? null;
  const [selectedCoachId, setSelectedCoachId] = React.useState<string | null>(null);

  // Seed the selection once from the guardian's saved favorite when the profile resolves.
  // Guarded so a late refetch (e.g. right after we save the favorite) can't clobber a
  // choice the guardian has since made.
  const coachSeededRef = React.useRef(false);
  React.useEffect(() => {
    if (coachSeededRef.current || !initialFavoriteCoachId) return;
    coachSeededRef.current = true;
    setSelectedCoachId(initialFavoriteCoachId);
  }, [initialFavoriteCoachId]);

  const handleSelectCoach = React.useCallback((id: string) => {
    coachSeededRef.current = true;
    setSelectedCoachId(id);
  }, []);

  // Step 4 notification opt-ins. The step component seeds itself from the guardian's
  // saved preferences and reports every change here so "Finish" can persist them.
  const initialNotificationPreferences = guardianProfile?.notificationPreferences;
  const notificationPreferencesRef = React.useRef<auth.NotificationPreferences | null>(null);
  const pendingPlayers = useOnboardingPlayersStore(s => s.pendingPlayers);
  const removePendingPlayer = useOnboardingPlayersStore(s => s.removePendingPlayer);
  const clearPendingPlayers = useOnboardingPlayersStore(s => s.clearPendingPlayers);
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  // Record the step outcome (PATCH /api/v1/guardians/me/onboarding) then advance.
  // The API step is 1-based; `currentStep` is 0-based. Only move on if the call
  // succeeds — the error interceptor surfaces failures.
  const advanceStep = React.useCallback(
    async (action: 'continue' | 'skip') => {
      try {
        showGlobalLoading();
        await updateOnboarding({ step: currentStep + 1, action });
        goNext();
      } catch {
        // Stay on the current step; the failure is already reported to the user.
      } finally {
        hideGlobalLoading();
      }
    },
    [showGlobalLoading, updateOnboarding, currentStep, goNext, hideGlobalLoading],
  );

  // Continuing step 1 also persists the profile (PATCH /api/v1/guardians/me)
  // before recording progress; advancing only happens once both calls succeed.
  // A freshly-picked local avatar is uploaded to Storage first and its URL sent
  // as `photoUrl` (mirrors the coach flow); a prefilled remote URL is left as-is.
  const submitProfile = React.useCallback(
    async (values: Parameters<typeof mapOnboardingProfileToGuardianRequest>[0]) => {
      try {
        showGlobalLoading();

        let photoUrl: string | undefined;
        const avatarUri = values.avatar?.uri;

        if (uid && shouldUploadAvatarUri(avatarUri)) {
          const respUpload = await uploadImageToStorage(
            avatarUri,
            `profile-pictures/guardian/${uid}.jpg`,
          );
          // Cache-bust with a version so the new image isn't masked by a stale cache.
          photoUrl = `${respUpload.downloadUrl}&v=${Date.now()}`;
        }

        await updateGuardian(mapOnboardingProfileToGuardianRequest(values, photoUrl));
        await updateOnboarding({ step: 1, action: 'continue' });
        goNext();
      } catch (e) {
        captureException(
          {
            error_payload: e,
          },
          { tags: { usk_log_type: 'submit_profile_error' } },
        );

        // Keep the user on step 1; the failure is already reported to them.
      } finally {
        hideGlobalLoading();
      }
    },
    [showGlobalLoading, uid, updateGuardian, updateOnboarding, goNext, hideGlobalLoading],
  );

  // Continuing step 2 creates every player the guardian queued in the add sheet
  // (POST /api/v1/guardians/me/players), each one removed from the queue as it
  // succeeds so a retry only re-sends the failures. Progress is recorded and the
  // wizard advances only once all of them save.
  const submitMatchPlayers = React.useCallback(async () => {
    try {
      showGlobalLoading();
      for (const player of pendingPlayers) {
        await createPlayer(player.payload);
        removePendingPlayer(player.id);
      }
      await updateOnboarding({ step: 2, action: 'continue' });
      goNext();
    } catch {
      // Stay on step 2; the failure is already reported to the user.
    } finally {
      hideGlobalLoading();
    }
  }, [
    showGlobalLoading,
    pendingPlayers,
    createPlayer,
    removePendingPlayer,
    updateOnboarding,
    goNext,
    hideGlobalLoading,
  ]);

  // Continuing step 3 saves the selected coach as a favorite
  // (POST /api/v1/guardians/me/favorite-coaches) before recording progress; the wizard
  // advances only once both calls succeed. With no coach selected, nothing is saved.
  const submitFindAcademy = React.useCallback(async () => {
    try {
      showGlobalLoading();
      if (selectedCoachId) {
        await addFavoriteCoaches({ coachIds: [selectedCoachId] });
      }
      await updateOnboarding({ step: 3, action: 'continue' });
      goNext();
    } catch {
      // Stay on step 3; the failure is already reported to the user.
    } finally {
      hideGlobalLoading();
    }
  }, [
    showGlobalLoading,
    selectedCoachId,
    addFavoriteCoaches,
    updateOnboarding,
    goNext,
    hideGlobalLoading,
  ]);

  // Finishing step 4 saves the notification opt-ins
  // (PATCH /api/v1/guardians/me/notification-preferences) before recording progress;
  // the wizard completes only once both calls succeed.
  const submitNotificationPreferences = React.useCallback(async () => {
    try {
      showGlobalLoading();
      const preferences = notificationPreferencesRef.current;
      if (preferences) {
        await updateNotificationPreferences(preferences);
      }
      await updateOnboarding({ step: 4, action: 'continue' });
      goNext();
    } catch {
      // Stay on step 4; the failure is already reported to the user.
    } finally {
      hideGlobalLoading();
    }
  }, [
    showGlobalLoading,
    updateNotificationPreferences,
    updateOnboarding,
    goNext,
    hideGlobalLoading,
  ]);

  // Step 1 (profile) validates + saves before advancing; step 2 saves the queued
  // players; step 3 saves the selected favorite coach; step 4 saves the notification
  // opt-ins; later steps just record progress.
  const handleContinue = React.useCallback(() => {
    if (currentStep === 0) {
      profileForm.handleSubmit(submitProfile)();
      return;
    }
    if (currentStep === 1) {
      submitMatchPlayers();
      return;
    }
    if (currentStep === 2) {
      submitFindAcademy();
      return;
    }
    if (currentStep === 3) {
      submitNotificationPreferences();
      return;
    }
    advanceStep('continue');
  }, [
    currentStep,
    profileForm,
    submitProfile,
    submitMatchPlayers,
    submitFindAcademy,
    submitNotificationPreferences,
    advanceStep,
  ]);

  // "Skip" bypasses the current step (no data saved) and moves to the next one.
  // Skipping step 1 resets the profile form back to the values prefilled from the
  // guardian's saved profile; skipping step 2 discards any queued players.
  const handleSkip = React.useCallback(() => {
    if (currentStep === 0) {
      profileForm.reset();
    }
    if (currentStep === 1) {
      clearPendingPlayers();
    }
    advanceStep('skip');
  }, [currentStep, profileForm, clearPendingPlayers, advanceStep]);

  const strings = STRINGS.onboarding;
  const top = insets.top > 0 ? insets.top + scale(16) : scale(50);

  const [coachLoadMoreSignal, setCoachLoadMoreSignal] = React.useState(0);
  const handleBodyScroll = React.useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (currentStep !== 2) return;
      const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
      const distanceToBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
      if (distanceToBottom < scale(240)) setCoachLoadMoreSignal(n => n + 1);
    },
    [currentStep, scale],
  );

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <OnboardingStepProfile />;
      case 1:
        return <OnboardingStepMatchPlayers avatarCacheBust={avatarCacheBust} />;
      case 2:
        return (
          <OnboardingStepFindAcademy
            selectedId={selectedCoachId}
            onSelect={handleSelectCoach}
            loadMoreSignal={coachLoadMoreSignal}
          />
        );
      default:
        return (
          <OnboardingStepStayInLoop
            initialPreferences={initialNotificationPreferences}
            onChange={preferences => {
              notificationPreferencesRef.current = preferences;
            }}
          />
        );
    }
  };

  return (
    <LinearGradient
      colors={BACKGROUND_GRADIENT}
      locations={BACKGROUND_LOCATIONS}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.background}>
      <FormProvider {...profileForm}>
        <Stack flex={1} align="center">
          <Stack flex={1} width={scale({ phone: 344, tablet: 384 })} style={{ paddingTop: top }}>
            {/* Stepper + Skip */}
            <Stack direction="row" align="center" justify="space-between" gap={scale(16)}>
              <OnboardingStepper currentStep={currentStep} totalSteps={totalSteps} />
              <Pressable onPress={handleSkip} hitSlop={8}>
                <Text variant="Font Guardian App/Body/Body 16px Regular" style={styles.skip}>
                  {strings.skip}
                </Text>
              </Pressable>
            </Stack>

            {/* Step content — only the scrollable body avoids the keyboard, so the
                footer is left in place (the keyboard overlays it) rather than lifted. */}
            <KeyboardAvoidingView
              behavior={isWindowed ? undefined : 'padding'}
              style={styles.keyboardAvoidingView}>
              <Scroll
                showsVerticalScrollIndicator={false}
                contentContainerStyle={styles.bodyContent}
                onScroll={handleBodyScroll}
                scrollEventThrottle={16}
                refreshControl={
                  // Pull-to-refresh only on step 2 (players) and only on native — RN's
                  // RefreshControl has no effect on web.
                  Platform.OS !== 'web' && currentStep === 1 ? (
                    <RefreshControl
                      refreshing={refreshingPlayers}
                      onRefresh={handleRefreshPlayers}
                    />
                  ) : undefined
                }>
                {renderStep()}
              </Scroll>
            </KeyboardAvoidingView>

            {/* Footer — outside KeyboardAvoidingView so it stays pinned and the
                keyboard covers it instead of pushing it up. */}
            <Stack
              style={{
                paddingTop: scale(12),
                paddingBottom: insets.bottom + (insets.bottom <= 0 ? scale(12) : 0),
              }}>
              <OnboardingFooter
                onContinue={handleContinue}
                showBack={!isFirstStep}
                onBack={goBack}
                continueLabel={isLastStep ? strings.step4.finish : undefined}
              />
            </Stack>
          </Stack>
        </Stack>
      </FormProvider>
    </LinearGradient>
  );
}

const useStyles = createStyles(({ scale }) => ({
  background: {
    flex: 1,
  },
  keyboardAvoidingView: {
    flex: 1,
    width: '100%',
  },
  skip: {
    color: colors.Blue,
  },
  bodyContent: {
    paddingTop: scale(20),
    paddingBottom: scale(16),
  },
}));

export default OnboardingScreen;
